import React, { useState } from 'react';
import { useMutation } from '@apollo/client';
import { SET_TRANSACTION_HIDDEN } from '../graphql/mutations/transactions';
import TransactionRow, { TransactionMobileCard } from './TransactionRow';
import TransactionModal from './TransactionModal';
import DeleteTransactionModal from './DeleteTransactionModal';
import MoveTransactionModal from './MoveTransactionModal';
import AssignLocationModal from './AssignLocationModal';
import { TransactionType } from '../utils/enums';
// formatters no longer needed after removing collapsed summary

const TransactionList = ({ transactions, token, currentPrice, tokenId, updateCache, portfolioId, refetch, holdingsAmount, tokenLocations = [], locationBalances = [], onTransactionsChanged, onTransactionsMoved }) => {
  const [setHidden] = useMutation(SET_TRANSACTION_HIDDEN);

  const handleToggleEnabled = async (transaction) => {
    try {
      const nextHidden = !(transaction.hidden === true ? true : false);
      await setHidden({ variables: { transactionId: transaction.id, portfolioId, hidden: nextHidden } });
      await refetch?.();
      try { onTransactionsChanged && onTransactionsChanged(); } catch (_) {}
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Failed to toggle enabled', e);
    }
  };
  // States for modals
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isMoveModalOpen, setIsMoveModalOpen] = useState(false);
  const [isAssignLocationModalOpen, setIsAssignLocationModalOpen] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [movingBulk, setMovingBulk] = useState(false);
  const [expandedMobileTransactionId, setExpandedMobileTransactionId] = useState(null);

  // Handle edit button click
  const handleEdit = (transaction) => {
    setSelectedTransaction(transaction);
    setIsEditModalOpen(true);
  };

  // Handle delete button click
  const handleDelete = (transaction) => {
    setSelectedTransaction(transaction);
    setIsDeleteModalOpen(true);
  };

  // Handle move button click (single)
  const handleMove = (transaction) => {
    setMovingBulk(false);
    setSelectedTransaction(transaction);
    setIsMoveModalOpen(true);
  };

  // Bulk move selected transactions
  const handleBulkMove = () => {
    setMovingBulk(true);
    setSelectedTransaction(null);
    setIsMoveModalOpen(true);
  };

  const handleBulkAssignLocation = () => {
    setSelectedTransaction(null);
    setIsAssignLocationModalOpen(true);
  };

  const handleToggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSelectAll = () => {
    if (selectedIds.size === transactions.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(transactions.map((tx) => tx.id)));
    }
  };

  const bulkTransactions = transactions.filter((tx) => selectedIds.has(tx.id));

  // Sort transactions by timestamp (newest first); if equal to the minute, fall back to createdAt
  const sortedTransactions = [...transactions].sort((a, b) => {
    const timeA = new Date(a.timestamp).getTime();
    const timeB = new Date(b.timestamp).getTime();
    if (timeB !== timeA) return timeB - timeA;
    const createdA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
    const createdB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
    if (createdB !== createdA) return createdB - createdA;
    const updatedA = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
    const updatedB = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
    if (updatedB !== updatedA) return updatedB - updatedA;
    // Final deterministic fallback
    return (b.id || '').localeCompare(a.id || '');
  });

  // Count transactions and calculate amounts by type
  const txCounts = {
    [TransactionType.BUY]: 0,
    [TransactionType.SELL]: 0,
    [TransactionType.TRANSFER_IN]: 0,
    [TransactionType.TRANSFER_OUT]: 0
  };

  transactions.forEach(tx => {
    const txType = tx.transactionType?.toUpperCase?.() || tx.transactionType;
    if (txCounts[txType] !== undefined) {
      txCounts[txType]++;
    }
  });

  // Collapsed summary removed; showing simple counts only

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4">
      {selectedIds.size > 0 && (
        <div className="mb-3 flex flex-wrap items-center gap-2 rounded-md bg-blue-50 dark:bg-blue-900/20 px-3 py-2">
          <button
            type="button"
            onClick={handleBulkMove}
            className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-1 text-xs font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
            </svg>
            Move {selectedIds.size} transaction{selectedIds.size === 1 ? '' : 's'}
          </button>
          <button
            type="button"
            onClick={handleBulkAssignLocation}
            className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-1 text-xs font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a2 2 0 01-2.828 0l-4.243-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            Assign location
          </button>
        </div>
      )}
      <div className="space-y-2 md:hidden">
        {sortedTransactions.length === 0 ? (
          <div className="px-2 py-3 text-center text-xs text-gray-500 dark:text-gray-400">
            No transactions found.
          </div>
        ) : (
          sortedTransactions.map(transaction => (
            <TransactionMobileCard
              key={transaction.id}
              transaction={transaction}
              token={token}
              currentPrice={currentPrice}
              tokenLocations={tokenLocations}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onMove={handleMove}
              onToggleEnabled={handleToggleEnabled}
              isSelected={selectedIds.has(transaction.id)}
              onToggleSelect={handleToggleSelect}
              isExpanded={expandedMobileTransactionId === transaction.id}
              onToggle={() => setExpandedMobileTransactionId((current) => current === transaction.id ? null : transaction.id)}
            />
          ))
        )}
      </div>
      <div className="hidden overflow-x-auto md:block">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead>
            <tr className="border-b dark:border-gray-700">
              <th className="py-1 pl-2 pr-1 w-8">
                <input
                  type="checkbox"
                  checked={transactions.length > 0 && selectedIds.size === transactions.length}
                  ref={(el) => { if (el) el.indeterminate = selectedIds.size > 0 && selectedIds.size < transactions.length; }}
                  onChange={handleSelectAll}
                  className="h-3.5 w-3.5 rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 cursor-pointer"
                />
              </th>
              <th className="py-1 text-left text-xs font-medium text-gray-500 dark:text-gray-400">Date</th>
              <th className="py-1 text-left text-xs font-medium text-gray-500 dark:text-gray-400">Type</th>
              <th className="py-1 text-right text-xs font-medium text-gray-500 dark:text-gray-400">Price</th>
              <th className="py-1 text-right text-xs font-medium text-gray-500 dark:text-gray-400">Quantity</th>
              <th className="py-1 text-right text-xs font-medium text-gray-500 dark:text-gray-400">Cost</th>
              <th className="py-1 text-right text-xs font-medium text-gray-500 dark:text-gray-400">Proceeds</th>
              <th className="py-1 text-right text-xs font-medium text-gray-500 dark:text-gray-400">Fees</th>
              <th className="py-1 text-right text-xs font-medium text-gray-500 dark:text-gray-400 whitespace-nowrap">Realized PnL</th>
              <th className="py-1 text-right text-xs font-medium text-gray-500 dark:text-gray-400 whitespace-nowrap">Unrealized PnL</th>
              <th className="py-1 text-right text-xs font-medium text-gray-500 dark:text-gray-400">Location</th>
              <th className="py-1 text-right text-xs font-medium text-gray-500 dark:text-gray-400">Notes</th>
              <th className="py-1 text-center text-xs font-medium text-gray-500 dark:text-gray-400">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {sortedTransactions.length === 0 ? (
              <tr>
                <td colSpan="13" className="px-2 py-3 text-center text-xs text-gray-500 dark:text-gray-400">
                  No transactions found.
                </td>
              </tr>
            ) : (
              sortedTransactions.map(transaction => (
                <TransactionRow 
                  key={transaction.id} 
                  transaction={transaction}
                  token={token}
                  currentPrice={currentPrice}
                  tokenLocations={tokenLocations}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  onMove={handleMove}
                  onToggleEnabled={handleToggleEnabled}
                  isSelected={selectedIds.has(transaction.id)}
                  onToggleSelect={handleToggleSelect}
                />
              ))
            )}
          </tbody>
        </table>
      </div>
      
      {/* Edit Transaction Modal */}
      <TransactionModal 
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        transaction={selectedTransaction}
        tokenId={tokenId}
        currentPrice={currentPrice}
        updateCache={updateCache}
        portfolioId={portfolioId}
        holdingsAmount={holdingsAmount}
        tokenLocations={tokenLocations}
        locationBalances={locationBalances}
        refetch={refetch}
        onSaved={() => { try { onTransactionsChanged && onTransactionsChanged(); } catch (_) {} }}
      />

      {/* Delete Transaction Modal */}
      <DeleteTransactionModal 
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        transaction={selectedTransaction}
        updateCache={updateCache}
        portfolioId={portfolioId}
        refetch={refetch}
        onDeleted={() => { try { onTransactionsChanged && onTransactionsChanged(); } catch (_) {} }}
      />

      {/* Move Transaction Modal */}
      <MoveTransactionModal
        isOpen={isMoveModalOpen}
        onClose={() => setIsMoveModalOpen(false)}
        transactions={movingBulk ? bulkTransactions : (selectedTransaction ? [selectedTransaction] : [])}
        portfolioId={portfolioId}
        sourceTokenId={tokenId}
        onMoved={(result, targetToken) => {
          if (movingBulk) setSelectedIds(new Set());
          refetch?.();
          try { onTransactionsChanged && onTransactionsChanged(); } catch (_) {}
          try { onTransactionsMoved && onTransactionsMoved(result, targetToken); } catch (_) {}
        }}
      />

      <AssignLocationModal
        isOpen={isAssignLocationModalOpen}
        onClose={() => setIsAssignLocationModalOpen(false)}
        transactions={bulkTransactions}
        portfolioId={portfolioId}
        tokenLocations={tokenLocations}
        onAssigned={(result) => {
          if (!result?.hasFailures) setSelectedIds(new Set());
          refetch?.();
          try { onTransactionsChanged && onTransactionsChanged(); } catch (_) {}
        }}
      />
    </div>
  );
};

export default TransactionList; 